import { Locale } from '@/providers/Locale/types'
import { ucfirst } from './ucfirst'

// Formats date time in format "Month Day, Year" (e.g., "May 20, 2025")
function formatDateTime(timestamp: string, locale: Locale): string {
    const now = new Date()
    let date = now
    if (timestamp) date = new Date(timestamp)

    return date.toLocaleDateString(locale, { month: 'long', year: 'numeric', day: '2-digit' })
}

// Formats date time in format "Weekday, Hour:Minute AM/PM" (e.g., "Thursday, 3:08PM")
function formatDateWithWeekdayAndTime(timestamp: string, locale: Locale): string {
    const now = new Date()
    let date = now
    if (timestamp) date = new Date(timestamp)

    return date.toLocaleString(locale, {
        weekday: 'long',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    })
}

const millisecondThresholds = {
    days: 1 * 24 * 60 * 60 * 1000,
    hours: 1 * 60 * 60 * 1000,
    minutes: 1 * 60 * 1000,
}

export function millisecondsToRelativeFormat(ms: number): [number, Intl.RelativeTimeFormatUnit] {
    if (ms > millisecondThresholds.days) {
        return [-Math.floor(ms / millisecondThresholds.days), 'days']
    } else if (ms > millisecondThresholds.hours) {
        return [-Math.floor(ms / millisecondThresholds.hours), 'hours']
    } else {
        return [-Math.floor(ms / millisecondThresholds.minutes), 'minutes']
    }
}

export function formatSeconds(totalSeconds: number) {
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    const formattedMinutes = String(minutes).padStart(2, '0')
    const formattedSeconds = String(seconds).padStart(2, '0')

    return `${formattedMinutes}:${formattedSeconds}`
}

export function formatRelativeTime(timestamp: string | null | undefined, locale: Locale) {
    if (!timestamp) return ''
    if (isNaN(Date.parse(timestamp))) return ''

    const date = new Date(timestamp)
    const now = new Date()
    const diffInMs = now.getTime() - date.getTime()
    const [value, unit] = millisecondsToRelativeFormat(diffInMs)

    if (value < -1 && value >= -5 && unit === 'days') {
        // Between 1 and 5 days ago, show "Weekday, Hour:Minute AM/PM"
        return formatDateWithWeekdayAndTime(timestamp, locale)
    } else if (value < -5 && unit === 'days') {
        // More than 5 days ago, show "Month, Day, Year"
        return formatDateTime(timestamp, locale)
    } else {
        // else format as relative time
        return ucfirst(new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(value, unit))
    }
}
